import { type ChangeEvent, type FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  type IconButtonProps,
  Popover,
  Radio,
  RadioGroup,
  useTheme,
} from '@mui/material';

import { BaseBtn, Icon, Row, SearchInput, Text } from '@/components/ui';
import { PlayArrow } from '@mui/icons-material';

import { type SxProps } from '@mui/system';
import { CustomFormControlLabel } from './CustomFormControlLabel';
import { clearFilter, home } from '@/assets';
import { useTranslation } from '@/hooks';
import { useAnchorEl } from '@/hooks/useAnchorEl';

interface ResetButtonProps extends IconButtonProps {
  showHomeButton?: boolean;
}

const ResetButton: FC<ResetButtonProps> = ({ onClick, sx, showHomeButton }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: 'absolute',
      zIndex: 2,
      height: '1.8125rem', // 29px
      width: '2.0625rem', // 33px
      ...sx,
    }}
  >
    <Icon src={showHomeButton ? home : clearFilter} />
  </IconButton>
);

interface Props {
  title?: string;
  items: { value: string; label: string }[];
  value?: string;
  onChange?: (value: string) => void;
  withSearch?: boolean;
  withConfirm?: boolean;
  hideAllButton?: boolean;
  showHomeButton?: boolean;
  defaultValue?: string;
  sx?: SxProps;
  sxBtn?: SxProps;
  sxLabel?: SxProps;
  showOnlyResetButton?: boolean;
  noSelectLabel?: string;
  formInput?: boolean;
  disabled?: boolean;
  hideValue?: boolean;
  hideResetButton?: boolean;
  controlsWidth?: number;
  withoutMargin?: boolean;
  big?: boolean;
  translate?: boolean;
  popoverMinWidth?: string;
  helperText?: string;
  error?: boolean;
}

export const BaseSelect: FC<Props> = ({
  sx,
  sxBtn,
  title,
  formInput,
  items,
  value,
  onChange,
  withSearch,
  hideAllButton,
  showHomeButton,
  defaultValue,
  showOnlyResetButton,
  noSelectLabel,
  disabled,
  hideValue,
  hideResetButton,
  controlsWidth,
  withoutMargin,
  big,
  translate,
  sxLabel,
  popoverMinWidth,
  helperText,
  error,
}) => {
  const { t } = useTranslation();
  const { isOpen, anchorEl, onOpenMenu, onCloseMenu } = useAnchorEl();

  // Lock scroll when dropdown is open to prevent background scrolling
  // useScrollLock(isOpen);
  const [valueItems, setValueItems] =
    useState<{ value: string; label: string }[]>(items);
  const [filterItems, setfilterItems] =
    useState<{ value: string; label: string }[]>(items);
  const handleChange = (value: string) => {
    onChange?.(value);
    onCloseMenu();
  };
  const { palette } = useTheme();

  useEffect(() => {
    setValueItems([...items]);
  }, [items]);

  const currentFilter = useMemo(
    () => valueItems.find(item => item.value == value),
    [value, valueItems]
  );
  const [search, setSearch] = useState('');
  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) =>
    setSearch(e.target.value);

  useEffect(() => {
    setfilterItems(
      valueItems.filter(i => {
        const findByLabel = i.label
          .toLowerCase()
          .includes(search.toLowerCase().trim());
        const findByValue = i.value
          .toLowerCase()
          .includes(search.toLowerCase().trim());
        return findByLabel || findByValue;
      })
    );
  }, [search, valueItems]);

  const handleClose = () => {
    onCloseMenu();
  };

  const HOME_VALUE = '87';

  const clearFilterValue = () => onChange?.('');

  const homeButtonClick = () => {
    if (defaultValue) {
      handleChange(defaultValue);
    }
  };

  const hasNumericValue = (val: string | undefined): boolean => {
    return Boolean(val && Number.isInteger(Number(val)));
  };

  const isHomeValue = (val: string | undefined): boolean => {
    return val === HOME_VALUE;
  };

  const isShowResetButton = () => {
    if (hideResetButton) return;
    return (
      value &&
      hasNumericValue(value) &&
      (isHomeValue(value) || showOnlyResetButton)
    );
  };

  const isShowHomeButton = () => {
    return (
      value &&
      hasNumericValue(value) &&
      !isHomeValue(value) &&
      !showOnlyResetButton
    );
  };
  return (
    <Box sx={{ mr: withoutMargin ? '' : '1.25rem', ...sx }}>
      {' '}
      {/* 20px */}
      {formInput ? (
        <Text
          noWrap
          sx={{
            fontSize: '0.875rem', // 14px
            fontWeight: 600,
            textTransform: 'capitalize',
            color: palette.primary.dark,
            marginBottom: '0.1875rem', // 3px
            marginTop: '0.1875rem', // 3px
            ...sxLabel,
          }}
        >
          {title}
        </Text>
      ) : (
        <Text
          noWrap
          sx={{
            color: 'info.dark',
            fontSize: '0.75rem', // 12px
            fontWeight: 500,
            pr: '0.125rem', // 2px
          }}
        >
          {title}
        </Text>
      )}
      <Row
        sx={{
          position: 'relative',
          justifyContent:
            value !== '' && hasNumericValue(value) ? 'space-between' : '',
        }}
      >
        {/* showHomeButton */}
        {showHomeButton && (
          <>
            {isShowHomeButton() && (
              <ResetButton showHomeButton onClick={homeButtonClick} />
            )}
          </>
        )}
        {
          <>
            {isShowResetButton() ? (
              <ResetButton onClick={clearFilterValue} />
            ) : null}
          </>
        }
        {/* {<>{value && !isNaN(parseInt(value)) ? <ResetButton onClick={clearFilterValue} /> : null}</>} */}
        {/* <ResetButton showHomeButton={!!showHomeButton} onClick={resetFilters} /> */}

        <BaseBtn
          btnColor='info.main'
          disabled={disabled}
          onClick={onOpenMenu}
          sx={{
            borderColor: formInput ? 'primary.dark' : palette.info.dark,
            opacity: '1 !important',
            height: big ? '3.0625rem' : '1.8125rem', // 49px : 29px
            minWidth: '100%',
            px: 0,
            bgcolor: disabled ? 'info.contrastText' : 'info.main',
            ...sxBtn,
          }}
        >
          <Text
            noWrap
            sx={{
              color: 'primary.dark',
              fontSize: '0.875rem', // 14px
              fontWeight: 500,
              width: 'calc(100% - 2.5rem)', // 40px
              pl:
                showHomeButton && !hideResetButton
                  ? value !== '' && items.length && hasNumericValue(value)
                    ? '2.1875rem' // 35px
                    : '0.625rem' // 10px
                  : '0.3125rem', // 5px
              textAlign: 'left',
            }}
          >
            {currentFilter?.value ? (
              <>
                {!hideValue && (
                  <>
                    <span> {currentFilter?.value} </span>
                  </>
                )}
                <span>
                  {translate ? t(currentFilter?.label) : currentFilter?.label}
                </span>
              </>
            ) : (
              <Box component='span' sx={{ color: '#d3d3d3 !important' }}>
                {noSelectLabel
                  ? noSelectLabel
                  : t('display_all_filter_base_select')}
              </Box>
            )}
          </Text>

          <PlayArrow
            sx={{
              m: '0.4375rem', // 7px
              fontSize: '0.875rem', // 14px
              color: formInput ? palette.info.dark : 'primary.dark',
              transform: `rotate(${isOpen ? '-90deg' : '90deg'})`,
            }}
          />
        </BaseBtn>
      </Row>
      {helperText && error && (
        <Text sx={{ color: 'error.main', fontSize: '0.75rem' }}>
          {helperText !== '' && t(`${helperText}_validation_text`)}
        </Text>
      )}
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableScrollLock={true}
        sx={{
          mt: '0.3125rem', // 5px
          '& .MuiPaper-root': {
            borderRadius: '0.75rem', // 12px
          },
        }}
      >
        <Box>
          {withSearch && (
            <SearchInput
              onfocus
              value={search}
              onChange={handleChangeSearch}
              placeholder={`${t('filter_search_placeholder')}`}
              noBorder
            />
          )}

          <Box
            sx={{
              borderTop: '1px solid grey',
              borderColor: withSearch ? 'info.dark' : 'info.main',
              minWidth: popoverMinWidth ? popoverMinWidth : '',
            }}
          >
            {filterItems.length === 0 ? (
              <Text
                sx={{
                  color: 'primary.main',
                  textAlign: 'center',
                  my: '0.625rem',
                }} // 10px
              >
                {t('filter_no_items')}
              </Text>
            ) : (
              <FormControl
                sx={{
                  width: controlsWidth ? `${controlsWidth / 16}rem` : '100%',
                }} // Convert controlsWidth px to rem
              >
                <RadioGroup value={value} sx={{ width: '100%' }}>
                  {!hideAllButton && (
                    <Box
                      sx={{
                        borderBottom: '1px solid black',
                        borderColor: 'primary.light',
                        px: '0.5rem', // 8px
                        mb: '0.125rem', // 2px
                      }}
                    >
                      <Box
                        onClick={() => handleChange('')}
                        sx={{
                          borderRadius: '0.625rem', // 10px
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'primary.light' },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Radio checked={value === ''} size='small' />
                          }
                          label={
                            <Text sx={{ color: 'black', pr: '0.625rem' }}>
                              {' '}
                              {/* 10px */}
                              {noSelectLabel
                                ? noSelectLabel
                                : t('display_all_filter_base_select')}
                            </Text>
                          }
                          sx={{
                            fontSize: '0.6875rem', // 11px
                            mx: 0,
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  <>
                    <Box
                      sx={{
                        maxHeight: '31.25rem', // 500px
                      }}
                    >
                      {defaultValue && (
                        <CustomFormControlLabel
                          sx={{
                            color: 'primary.dark',
                            fontWeight: '700',
                          }}
                          key={value}
                          onChange={() => handleChange(defaultValue)}
                          value={defaultValue}
                          selected
                          labels={{
                            value: defaultValue,
                            label:
                              items.find(item => item.value === defaultValue)
                                ?.label || '',
                          }}
                        />
                      )}
                      {filterItems.map(({ value, label }) => (
                        <CustomFormControlLabel
                          key={value}
                          hideValue={hideValue}
                          onChange={() => handleChange(value)}
                          value={value}
                          labels={{ value, label }}
                          translate={translate}
                        />
                      ))}
                    </Box>
                  </>
                </RadioGroup>
              </FormControl>
            )}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
